import { type CSSProperties, useCallback } from "react";

import { Trash2Icon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { cx, styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Popup } from "simplydo/react/ui/popup";
import { resolveUiColor, type UiColor } from "simplydo/react/ui/utils/colors";

import { ROLE_ICONS, isRoleIconName, type RoleIconName } from "../roleIcons";
import type { RoleSettingsUpdateBody } from "../types";
import {
  ROLE_EDITOR_COLOR_OPTIONS,
  ROLE_EDITOR_ICON_OPTIONS,
} from "./IconChooser.constants";

const IconChooserButtonGroup = styled.div`
  display: flex;
  justify-content: space-evenly;
  gap: 5px;
`;

const IconChooserButton = styled.div`
  cursor: pointer;
  border-radius: 5px;
  padding: 7.5px;

  &:hover:not(.is-selected) {
    background-color: #f6f6f6;
  }

  &.is-selected {
    background-color: #dfdfdf;
  }

  > svg.icon {
    margin: 0;
  }
`;

const ColorChooserButton = styled.div<{ $color: UiColor }>`
  cursor: pointer;
  border-radius: 5px;
  padding: 15px;
  background-color: ${({ $color, theme }) => resolveUiColor(theme, $color)};
  &:hover {
    // Grow slightly
    transform: scale(1.05);
  }
`;

type RoleEditorIconChooserProps = {
  containerStyle?: CSSProperties;
  role: OpenAPI.Schemas["Role"];
  updateRole: (data: RoleSettingsUpdateBody) => void;
};

const RoleEditorIconChooser = ({
  containerStyle = {},
  role,
  updateRole,
}: RoleEditorIconChooserProps) => {
  const { icon = { name: "", colour: "" } } = role ?? {};
  const { name: selectedIcon, colour: selectedColor } = icon;

  const setSelectedIcon = useCallback(
    (props: { name?: RoleIconName; colour?: UiColor }) => {
      if (props.name && !isRoleIconName(props.name)) return;
      updateRole({
        icon: { ...(role.icon ?? {}), name: props.name, colour: props.colour },
      });
    },
    [role, updateRole],
  );

  const removeIcon = useCallback(() => {
    updateRole({ icon: null });
  }, [updateRole]);

  return (
    <div style={containerStyle}>
      <IconChooserButtonGroup>
        {ROLE_EDITOR_ICON_OPTIONS.map((iconName) => (
          <div
            key={iconName}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Popup
              on="click"
              position="bottom center"
              trigger={
                <IconChooserButton
                  className={cx(selectedIcon === iconName && "is-selected")}
                >
                  <Icon
                    icon={ROLE_ICONS[iconName]}
                    size="large"
                    color={
                      iconName === selectedIcon ? selectedColor : undefined
                    }
                  />
                </IconChooserButton>
              }
            >
              {
                <IconChooserButtonGroup>
                  {ROLE_EDITOR_COLOR_OPTIONS.map((color) => (
                    <ColorChooserButton
                      key={color}
                      $color={color}
                      onClick={() => {
                        setSelectedIcon({ name: iconName, colour: color });
                      }}
                    />
                  ))}
                </IconChooserButtonGroup>
              }
            </Popup>
            {selectedIcon == iconName ? (
              <Button
                size="small"
                onClick={() => removeIcon()}
                style={{ marginTop: 10 }}
                variant="outline"
              >
                <Icon icon={Trash2Icon} />
                Remove icon
              </Button>
            ) : null}
          </div>
        ))}
      </IconChooserButtonGroup>
    </div>
  );
};

export default RoleEditorIconChooser;
